<template>
  <div>
    <el-dialog
      v-model="currentVisible"
      title=""
      append-to-body
      :fullscreen="true"
      :show-header="false"
      :show-footer="false"
      :show-close="false"
      @close="isFullScreenFn"
    >
      <div class="fullscreen-content">
        <!-- 图片容器（用于拖拽和缩放） -->
        <div
          class="image-wrapper"
          @wheel.prevent="handleWheel"
          @mousedown="handleMouseDown"
        >
          <img
            :src="image"
            alt=""
            class="full-image"
            :style="{
              transform: `translate(${x}px, ${y}px) scale(${scale})`,
              transition: isDragging ? 'none' : 'transform 0.2s ease',
            }"
            @mousemove="handleMouseMove"
            @mouseup="handleMouseUp"
            @mouseleave="handleMouseUp"
          >
        </div>
        <!-- 3. 操作工具栏（放大/缩小/重置/切换） -->
        <div class="operation-bar">
          <!-- 原生按钮不继承外层表单的禁用状态，详情模式仍可操作图片预览。 -->
          <button
            type="button"
            class="operation-button"
            aria-label="放大图片"
            @click="scaleUp"
          >
            <el-icon><ZoomIn /></el-icon>
          </button>
          <button
            type="button"
            class="operation-button"
            :disabled="scale <= 1"
            aria-label="缩小图片"
            @click="scaleDown"
          >
            <el-icon><ZoomOut /></el-icon>
          </button>
          <button
            type="button"
            class="operation-button"
            aria-label="重置图片"
            @click="resetScale"
          >
            <el-icon><Refresh /></el-icon>
          </button>
          <button
            type="button"
            class="operation-button close-btn"
            aria-label="关闭预览"
            @click="isFullScreenFn"
          >
            <el-icon><Close /></el-icon>
          </button>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
const props = defineProps({
  isFullScreen: {
    type: Boolean,
    default: false,
  },
  image: {
    type: String,
  },
});
const emit = defineEmits(['update:isFullScreen', 'isFullScreenFn']);

const scale = ref(1);
const x = ref(0);
const y = ref(0);
const startX = ref(0);
const startY = ref(0);
const isDragging = ref(false);

// 本地维护可见性状态
const currentVisible = ref(props.isFullScreen);
// 监听父组件传入的props变化
watch(
  () => props.isFullScreen,
  (newVal) => {
    currentVisible.value = newVal;
  },
);

// 监听本地状态变化，通知父组件
watch(currentVisible, (newVal) => {
  emit('update:isFullScreen', newVal);
});

// methods: {
function isFullScreenFn() {
  emit('isFullScreenFn');
  resetScale(); // 每次打开重置缩放和偏移
}

// 2. 缩放相关方法
// 放大（步长 0.2，最大 5 倍，避免过度缩放）
function scaleUp() {
  if (scale.value < 5) scale.value += 0.2;
}
// 缩小（步长 0.2，最小 1 倍，避免小于原图）
function scaleDown() {
  if (scale.value > 1) scale.value -= 0.2;
}
// 重置缩放和偏移
function resetScale() {
  scale.value = 1;
  x.value = 0;
  y.value = 0;
}
// 鼠标滚轮缩放（向上滚放大，向下滚动缩小）
function handleWheel(e) {
  const delta = e.deltaY > 0 ? -0.1 : 0.1; // deltaY 正：向下滚，负：向上滚
  scale.value = Math.max(1, Math.min(5, scale.value + delta)); // 限制 1-5 倍
}

// 3. 拖拽相关方法
// 鼠标按下：记录初始位置
function handleMouseDown(e) {
  if (scale.value <= 1) return; // 原图状态不允许拖拽（避免多余操作）
  isDragging.value = true;
  startX.value = e.clientX - x.value; // 初始 X = 鼠标X - 当前偏移X
  startY.value = e.clientY - y.value; // 初始 Y = 鼠标Y - 当前偏移Y
}
// 鼠标移动：计算新偏移
function handleMouseMove(e) {
  if (!isDragging.value) return;
  x.value = e.clientX - startX.value;
  y.value = e.clientY - startY.value;
}
// 鼠标松开/离开：结束拖拽
function handleMouseUp() {
  isDragging.value = false;
}
// }
// }
</script>

<style scoped>
:deep(.el-dialog__body) {
  padding: 0 !important;
}

:deep(.el-dialog) {
  background: rgba(0, 0, 0, 0.5) !important;
}

/* 缩略图样式 */
.thumbnail {
  width: 200px;
  height: 150px;
  object-fit: cover;
  margin: 10px;
  cursor: zoom-in;
  border: 1px solid #eee;
  transition: border-color 0.3s;
}

.thumbnail:hover {
  border-color: #409eff;
  /* Element 主题色 */
}

/* 全屏内容容器 */
.fullscreen-content {
  position: relative;
  width: 100vw;
  height: calc(100vh - 60px);
  overflow: hidden;
}

/* 图片容器（拖拽缩放的载体） */
.image-wrapper {
  position: absolute;
  top: 50%;
  inset-inline-start: 50%;
  transform: translate(-50%, -50%) !important;
  /* transform-origin: center center; */
  /* 缩放原点：图片中心 */
  /* cursor: grab; */
  /* transform: scale(-50%,-50%); */
}

.image-wrapper:active {
  cursor: grabbing;
  /* 拖拽中光标变化 */
}

/* 全屏图片样式 */
.full-image {
  max-width: none;
  /* 取消默认最大宽度限制（用于缩放） */
  max-height: none;
}

/* 操作工具栏（固定在底部） */
.operation-bar {
  position: fixed;
  bottom: 30px;
  inset-inline-start: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.5);
  padding: 10px 20px;
  border-radius: 40px;
  display: flex;
  gap: 15px;
}

.operation-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  color: #fff;
  font-size: 30px;
  cursor: pointer;
  background: transparent;
  border: none;
}

.operation-button:hover {
  color: var(--el-color-primary);
}

.operation-button:disabled {
  color: var(--el-text-color-placeholder);
  cursor: not-allowed;
}

.close-btn {
  margin-inline-start: 20px;
}
</style>
